import { useMemo } from 'react';
import { parseMoney, sum, type Money } from '@mybooks/ledger';
import { api, ApiError, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import { fmtMoney, money } from '../../lib/format';

export type Contact = Schemas['Contact'];
export type ContactType = 'customer' | 'vendor' | 'both';
export type Item = Schemas['Item'];
export type TaxRate = Schemas['TaxRate'];

export const TYPE_LABEL: Record<ContactType, string> = { customer: 'Customer', vendor: 'Supplier', both: 'Customer and supplier' };

export const COUNTRIES: [string, string][] = [['US', 'United States'], ['CA', 'Canada'], ['MX', 'Mexico'], ['GB', 'United Kingdom'], ['ZA', 'South Africa'], ['DE', 'Germany'], ['RO', 'Romania']];
/** The country list, plus a saved code that is not in it, so editing never silently changes it. */
export const countryOptions = (current: string | null | undefined): [string, string][] =>
  current && !COUNTRIES.some(([c]) => c === current) ? [...COUNTRIES, [current, current]] : COUNTRIES;

export const CUR_NAME: Record<string, string> = { USD: 'US dollar', ZAR: 'South African rand', EUR: 'Euro', GBP: 'Pound sterling', CAD: 'Canadian dollar', MXN: 'Mexican peso', AUD: 'Australian dollar', NZD: 'New Zealand dollar', CHF: 'Swiss franc', JPY: 'Japanese yen', CNY: 'Chinese yuan', INR: 'Indian rupee', SGD: 'Singapore dollar', AED: 'UAE dirham', KES: 'Kenyan shilling', NGN: 'Nigerian naira', BWP: 'Botswana pula', NAD: 'Namibian dollar', MZN: 'Mozambican metical' };

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface Address { address_line1?: string | null; address_line2?: string | null; city?: string | null; region?: string | null; postal_code?: string | null; country?: string | null }
/** Address lines as the prototype prints them; the country only when it is not the US. */
export function addressLines(a: Address): string[] {
  return [
    (a.address_line1 ?? '') + (a.address_line2 ? ', ' + a.address_line2 : ''),
    [a.city, [a.region, a.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', '),
    a.country && a.country !== 'US' ? a.country : '',
  ].filter((s) => s.trim() !== '');
}
export const addressOf = (a: Address) => addressLines(a).join(', ');

export const termsText = (days: number | undefined) => (days ? `Net ${days}` : 'On receipt');

/** Blank text means "clear this" to the API. */
export const orNull = (s: string) => (s.trim() === '' ? null : s.trim());

/**
 * Server validation messages look like "email: Invalid email". Returns the field and its message
 * when the field is one the form shows, so the error can sit under the input.
 */
export function fieldOf(error: unknown, fields: readonly string[]): { field: string; message: string } | null {
  if (!(error instanceof ApiError) || error.code !== 'validation_error') return null;
  const m = /^([a-z_0-9]+): (.*)$/.exec(error.message);
  if (!m || !fields.includes(m[1] as string)) return null;
  return { field: m[1] as string, message: (m[2] as string).replace(/^./, (c) => c.toUpperCase()) };
}

/** "8.2500" → "8.25", "7.0000" → "7". Rates are text, never floats. */
export function rateText(rate: string | undefined): string {
  if (!rate) return '0';
  const [whole, frac = ''] = rate.split('.');
  const f = frac.replace(/0+$/, '');
  return f ? `${whole}.${f}` : (whole as string);
}
/** Mirrors the API's rate rule: up to 3 digits, 4 places, at most 100. */
export function validRate(text: string): boolean {
  const m = /^(\d{1,3})(?:\.(\d{1,4}))?$/.exec(text.trim());
  if (!m) return false;
  const whole = Number(m[1]);
  return whole < 100 || (whole === 100 && /^0*$/.test(m[2] ?? ''));
}

/** A unit price with cents, or up to 4 places when it has them: $89.99, $0.1250. */
export function fmtUnit(value: string | undefined, currency: string): string {
  const units = money(value);
  const whole = units / 10000n, frac = (units % 10000n).toString().padStart(4, '0');
  const shown = frac.slice(2) === '00' ? frac.slice(0, 2) : frac.replace(/0+$/, '');
  return fmtMoney(whole * 10000n, currency).replace(/\.00$/, '.' + shown);
}
/** "150.0000" → "150.00" for editing; keeps extra places when they matter. */
export function priceText(value: string | undefined): string {
  if (!value) return '';
  const [whole, frac = ''] = value.split('.');
  const f = frac.padEnd(4, '0');
  return `${whole}.${f.slice(2) === '00' ? f.slice(0, 2) : f.replace(/0+$/, '')}`;
}

// ---- Balances from the aging reports ----
export interface Balance { open: Money; overdue: Money }
/** An aging row: one per contact and currency (ADR 0029); its bucket columns are always in base. */
export type AgingRow = { contact_id: string; currency?: string | undefined; current: string; days_1_30: string; days_31_60: string; days_61_90: string; over_90: string; credits: string; total: string };

/**
 * Each contact's open and overdue amounts in base. A contact with documents in two currencies has
 * two rows; they are added together, never one replacing the other.
 */
export function balancesFrom(rows: readonly AgingRow[]): Map<string, Balance> {
  const map = new Map<string, Balance>();
  for (const r of rows) {
    const prev = map.get(r.contact_id) ?? { open: 0n, overdue: 0n };
    map.set(r.contact_id, {
      open: prev.open + parseMoney(r.total),
      overdue: prev.overdue + sum([r.days_1_30, r.days_31_60, r.days_61_90, r.over_90].map((v) => parseMoney(v))),
    });
  }
  return map;
}

/**
 * The currencies a contact deals in, base first then the rest alphabetically, from its documents
 * and aging rows. A statement needs a currency picker when this holds more than the base.
 */
export function contactCurrencies(records: readonly { currency?: string | null | undefined }[], base: string): string[] {
  const set = new Set<string>();
  for (const r of records) if (r.currency && r.currency !== base) set.add(r.currency);
  return [base, ...[...set].sort()];
}

const fetchAgingRows = async (side: 'receivable' | 'payable') => {
  const res = side === 'receivable' ? await unwrap(api.GET('/v1/reports/receivables-aging', {})) : await unwrap(api.GET('/v1/reports/payables-aging', {}));
  return ((res as { data?: { rows?: unknown[] } }).data?.rows ?? []) as AgingRow[];
};

/** Today's aging rows for one side: one per contact and currency. */
export function useAgingRows(side: 'receivable' | 'payable') {
  return useApiQuery(['reports', `${side}-aging-rows`], () => fetchAgingRows(side));
}

/**
 * What each contact owes (receivable) or is owed (payable) today, in base, from the aging report:
 * open is the net total after unused credits, overdue the part past its due date.
 */
export function useBalances(side: 'receivable' | 'payable') {
  const q = useAgingRows(side);
  const data = useMemo(() => (q.data ? balancesFrom(q.data) : undefined), [q.data]);
  return { data, isLoading: q.isLoading, isError: q.isError, error: q.error, refetch: q.refetch };
}
